'use client';

// Galexia Skills — browse the shared skill set and teach the agent your own.
//
// Layout ported from the approved standalone redesign (variant 1a: compact
// hero + icon-chip card grid with clear Added/Add states, filter pills with
// counts) with variant 1b's "SKILL OF THE WEEK" spotlight as the hero's
// right side. Violet-accented, theme-token based, inside the app's floating
// rounded-2xl panel shell.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ai-elements/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDocumentTitle } from '@/lib/use-document-title';
import {
  ChartLineUp,
  ChatCircleText,
  Check,
  Cube,
  FilmSlate,
  Lightning,
  PencilSimple,
  Plus,
  SealCheck,
  Sparkle,
  Trash,
  UploadSimple,
} from '@phosphor-icons/react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SharedSkill {
  id?: string;
  slug: string;
  name: string;
  summary: string;
}

interface UserSkill {
  id: string;
  slug: string;
  name: string;
  summary: string;
  content: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

type TabId = 'explore' | 'mine';
type OriginFilter = 'all' | 'builtin' | 'curated';

// ---------------------------------------------------------------------------
// Deterministic icon chips (design ref: 40px tinted chip + colored icon)
// ---------------------------------------------------------------------------

const CHIP_PRESETS = [
  { bg: 'bg-emerald-500/15', text: 'text-emerald-400', Icon: FilmSlate },
  { bg: 'bg-orange-500/15', text: 'text-orange-400', Icon: Lightning },
  { bg: 'bg-sky-500/15', text: 'text-sky-400', Icon: Cube },
  { bg: 'bg-lime-500/15', text: 'text-lime-400', Icon: ChatCircleText },
  { bg: 'bg-violet-500/15', text: 'text-violet-400', Icon: Sparkle },
  { bg: 'bg-amber-500/15', text: 'text-amber-400', Icon: ChartLineUp },
] as const;

function chipPresetFor(slug: string) {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = (hash * 131 + slug.charCodeAt(i)) >>> 0;
  }
  return CHIP_PRESETS[hash % CHIP_PRESETS.length];
}

// Card titles must be SHORT (the reference never wraps three lines). Builtin
// names are long technical strings — take the segment before the first dash;
// prettify camelCase; fall back to title-cased slug.
function shortTitleFor(name: string, slug: string) {
  let cut = name.split(/\s+[—–-]\s+/)[0]?.trim() ?? '';
  if (/^[a-z]+[A-Z]/.test(cut)) {
    cut = cut.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
    cut = cut.charAt(0).toUpperCase() + cut.slice(1);
  }
  if (cut && cut.length >= 3 && cut.length <= 40) return cut;
  if (cut && cut.length > 40) return `${cut.slice(0, 39)}…`;
  return slug
    .split(/[-_]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function chatHref(prompt: string) {
  return `/?q=${encodeURIComponent(prompt)}`;
}

// ---------------------------------------------------------------------------
// Card (design ref 1a: chip + affordance / name + verified / summary / meta)
// ---------------------------------------------------------------------------

function SkillCard({
  slug,
  name,
  summary,
  originLabel,
  verified,
  affordance,
  hoverActions,
  dimmed,
  metaRight,
}: {
  slug: string;
  name: string;
  summary: string;
  /** Mono uppercase origin in the footer: BUILT-IN / CURATED / YOURS. */
  originLabel: string;
  /** Blue seal next to the name (built-in skills). */
  verified?: boolean;
  /** Top-right: "Included" pill (shared) or Switch (mine). */
  affordance: React.ReactNode;
  /** Extra buttons revealed on hover next to the affordance (edit/delete). */
  hoverActions?: React.ReactNode;
  dimmed?: boolean;
  metaRight?: string;
}) {
  const title = shortTitleFor(name, slug);
  return (
    <article
      title={name}
      className={`group flex flex-col gap-3 rounded-2xl border border-border/60 bg-card/60 p-[18px] transition-all duration-150 hover:-translate-y-0.5 hover:border-border ${dimmed ? 'opacity-55 saturate-50' : ''}`}
    >
      <div className="flex items-start justify-between gap-2">
        <SkillChip slug={slug} />
        <div className="flex items-center gap-1.5">
          {hoverActions && (
            <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
              {hoverActions}
            </div>
          )}
          {affordance}
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <span className="text-[15.5px] font-extrabold tracking-tight">{title}</span>
          {verified && <SealCheck weight="fill" className="size-3.5 shrink-0 text-sky-400" aria-label="Built-in skill" />}
        </div>
        <span className="line-clamp-2 text-[12.5px] leading-relaxed text-muted-foreground">{summary}</span>
      </div>

      <div className="mt-auto flex items-center gap-2 pt-1">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/60">{originLabel}</span>
        {metaRight && (
          <span className="ml-auto font-mono text-[10.5px] text-muted-foreground/80">{metaRight}</span>
        )}
      </div>
    </article>
  );
}

function SkillChip({ slug, size = 'md' }: { slug: string; size?: 'md' | 'lg' }) {
  const preset = chipPresetFor(slug);
  const Icon = preset.Icon;
  return (
    <div className={`grid shrink-0 place-items-center rounded-xl ${preset.bg} ${size === 'lg' ? 'size-11 rounded-[13px]' : 'size-10'}`}>
      <Icon weight="duotone" className={`${size === 'lg' ? 'size-[21px]' : 'size-5'} ${preset.text}`} />
    </div>
  );
}

/** The violet "Included" state pill (design ref: the "Added" pill). */
function IncludedPill() {
  return (
    <span
      title="Included — always active"
      className="flex items-center gap-1.5 rounded-full bg-violet-500/10 px-2.5 py-1 text-[11.5px] font-extrabold text-violet-400"
    >
      <Check weight="bold" className="size-3" />
      Included
    </span>
  );
}

function RoundIconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="grid size-7 place-items-center rounded-full border border-border/70 text-muted-foreground transition-colors hover:border-border hover:text-foreground"
    >
      {children}
    </button>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="rounded-2xl border border-border/60 p-[18px]">
          <div className="flex items-start justify-between">
            <Skeleton className="size-10 rounded-xl" />
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
          <Skeleton className="mt-4 h-4 w-2/3" />
          <Skeleton className="mt-2 h-3 w-full" />
          <Skeleton className="mt-1.5 h-3 w-4/5" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function SkillsClient() {
  useDocumentTitle('Skills');

  const [builtin, setBuiltin] = useState<SharedSkill[]>([]);
  const [curated, setCurated] = useState<SharedSkill[]>([]);
  const [mine, setMine] = useState<UserSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>('explore');
  const [originFilter, setOriginFilter] = useState<OriginFilter>('all');

  // Dialog state (create + edit share it)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<UserSkill | null>(null);
  const [formName, setFormName] = useState('');
  const [formSummary, setFormSummary] = useState('');
  const [formContent, setFormContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const fetchSkills = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/skills');
      if (!response.ok) throw new Error(`Failed to load skills (${response.status})`);
      const data = await response.json();
      setBuiltin(Array.isArray(data.builtin) ? data.builtin : []);
      setCurated(Array.isArray(data.curated) ? data.curated : []);
      setMine(Array.isArray(data.mine) ? data.mine : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load skills');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  const openCreateDialog = () => {
    setEditing(null);
    setFormName('');
    setFormSummary('');
    setFormContent('');
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEditDialog = (skill: UserSkill) => {
    setEditing(skill);
    setFormName(skill.name);
    setFormSummary(skill.summary);
    setFormContent(skill.content);
    setDialogError(null);
    setDialogOpen(true);
  };

  // Upload .md → fill the textarea; frontmatter-style name:/description:
  // lines prefill the empty fields.
  const handleUploadFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      const nameMatch = text.match(/^name:\s*(.+)$/m);
      const descMatch = text.match(/^description:\s*(.+)$/m);
      setFormContent(text);
      if (nameMatch?.[1]) {
        setFormName((prev) => prev.trim() ? prev : nameMatch[1].trim().replace(/^["']|["']$/g, ''));
      }
      if (descMatch?.[1]) {
        setFormSummary((prev) => prev.trim() ? prev : descMatch[1].trim().replace(/^["']|["']$/g, ''));
      }
    };
    reader.readAsText(file);
  };

  const handleSave = async () => {
    setSaving(true);
    setDialogError(null);
    try {
      const payload = editing
        ? { id: editing.id, name: formName.trim(), summary: formSummary.trim(), content: formContent }
        : { name: formName.trim(), summary: formSummary.trim(), content: formContent };
      const response = await fetch('/api/skills', {
        method: editing ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        const message = typeof data?.error === 'string' ? data.error : `Save failed (${response.status})`;
        throw new Error(message);
      }
      setNotice(editing ? `Updated “${formName.trim()}”.` : `Saved “${formName.trim()}” — Galexia can use it now.`);
      setDialogOpen(false);
      setTab('mine');
      fetchSkills();
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (skill: UserSkill, isActive: boolean) => {
    // Optimistic flip; revert on failure.
    setMine((prev) => prev.map((s) => (s.id === skill.id ? { ...s, isActive } : s)));
    try {
      const response = await fetch('/api/skills', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: skill.id, isActive }),
      });
      if (!response.ok) throw new Error(`Toggle failed (${response.status})`);
    } catch (err) {
      setMine((prev) => prev.map((s) => (s.id === skill.id ? { ...s, isActive: !isActive } : s)));
      setError(err instanceof Error ? err.message : 'Toggle failed');
    }
  };

  const handleDelete = async (skill: UserSkill) => {
    if (!window.confirm(`Delete “${skill.name}”? Galexia will no longer use this skill.`)) return;
    try {
      const response = await fetch(`/api/skills?id=${encodeURIComponent(skill.id)}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(`Delete failed (${response.status})`);
      setMine((prev) => prev.filter((s) => s.id !== skill.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const exploreSkills: SharedSkill[] = useMemo(() => (
    originFilter === 'builtin' ? builtin : originFilter === 'curated' ? curated : [...builtin, ...curated]
  ), [originFilter, builtin, curated]);
  const builtinSlugs = useMemo(() => new Set(builtin.map((s) => s.slug)), [builtin]);

  // Skill of the week — deterministic rotation over the shared set.
  const spotlight = useMemo(() => {
    const pool = [...builtin, ...curated];
    if (pool.length === 0) return null;
    const week = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000));
    return pool[week % pool.length];
  }, [builtin, curated]);

  const filterPills: Array<{ id: OriginFilter; label: string; count: number }> = [
    { id: 'all', label: 'All', count: builtin.length + curated.length },
    { id: 'builtin', label: 'Built-in', count: builtin.length },
    ...(curated.length > 0 ? [{ id: 'curated' as const, label: 'Curated', count: curated.length }] : []),
  ];

  return (
    <div className="min-h-screen bg-background p-2 md:p-3">
      {/* Floating panel */}
      <div className="relative flex min-h-[calc(100vh-1.5rem)] flex-col overflow-hidden rounded-2xl border border-border/80 bg-card/40">

        {/* Sticky header — title + pill nav left, CTA right (design ref) */}
        <header className="sticky top-0 z-30 flex items-center gap-4 border-b border-border/60 bg-background/80 px-4 py-3 backdrop-blur-md md:px-6">
          <div className="flex items-center gap-2">
            <Lightning weight="fill" className="size-4 text-violet-400" />
            <h1 className="text-sm font-extrabold tracking-tight">Skills</h1>
          </div>
          <nav className="flex items-center gap-0.5 rounded-full border border-border/70 bg-card p-[3px]" role="tablist" aria-label="Skill tabs">
            {([
              { id: 'explore' as const, label: 'Explore' },
              { id: 'mine' as const, label: mine.length > 0 ? `My skills · ${mine.length}` : 'My skills' },
            ]).map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                className={`rounded-full px-4 py-1.5 text-[13px] font-semibold transition-colors ${tab === t.id ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {t.label}
              </button>
            ))}
          </nav>
          <div className="ml-auto">
            <Button
              size="sm"
              className="h-8 rounded-full bg-violet-500 px-4 text-xs font-extrabold text-white hover:bg-violet-400"
              onClick={openCreateDialog}
            >
              <Plus className="mr-1 size-3.5" weight="bold" />
              New skill
            </Button>
          </div>
        </header>

        {/* Compact hero: pitch left, SKILL OF THE WEEK spotlight right */}
        <section className="grid items-center gap-8 border-b border-border/40 px-4 py-8 md:px-6 lg:grid-cols-[1fr_420px] lg:gap-10">
          <div className="flex max-w-xl flex-col gap-3.5">
            <h2 className="text-[28px] font-extrabold leading-[1.1] tracking-tight md:text-[34px]">
              Ready-made abilities<br />Galexia learns and combines
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground md:text-[14.5px]">
              A skill is one reusable playbook, like structuring a pitch deck. Skills
              load automatically when the task matches, and your own skills join the
              built-in set the moment you save them.
            </p>
            <div className="mt-1.5 flex gap-2.5">
              <Button
                asChild
                variant="outline"
                size="sm"
                className="h-9 rounded-full border-violet-500/40 px-4 text-[13px] font-bold text-violet-400 hover:bg-violet-500/10 hover:text-violet-300"
              >
                <Link href={chatHref('Help me create a new skill for you to use. Ask me what craft or playbook I want you to learn.')}>
                  <Sparkle className="mr-1.5 size-3.5" weight="duotone" />
                  Create a skill in chat
                </Link>
              </Button>
            </div>
          </div>

          {spotlight && (
            <div className="relative overflow-hidden rounded-[18px] border border-border/70 bg-gradient-to-br from-violet-500/[0.09] via-card to-card p-6">
              <div className="flex flex-col gap-2.5">
                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-violet-400">
                  Skill of the week
                </span>
                <div className="flex items-center gap-2.5">
                  <SkillChip slug={spotlight.slug} size="lg" />
                  <div className="flex min-w-0 flex-col">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-lg font-extrabold tracking-tight">
                        {shortTitleFor(spotlight.name, spotlight.slug)}
                      </span>
                      <SealCheck weight="fill" className="size-3.5 shrink-0 text-sky-400" />
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70">
                      {builtinSlugs.has(spotlight.slug) ? 'Built-in' : 'Curated'} · always active
                    </span>
                  </div>
                </div>
                <p className="line-clamp-3 text-[13px] leading-relaxed text-muted-foreground">
                  {spotlight.summary}
                </p>
                <div className="mt-1">
                  <Button
                    asChild
                    size="sm"
                    className="h-9 rounded-full bg-violet-500 px-5 text-[13px] font-extrabold text-white hover:bg-violet-400"
                  >
                    <Link href={chatHref(`Use your "${shortTitleFor(spotlight.name, spotlight.slug)}" skill to help me with my next request.`)}>
                      Use it in chat
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Main */}
        <main className="min-w-0 flex-1 px-4 py-4 md:px-6">
          {notice && (
            <p className="mb-4 rounded-lg border border-violet-500/25 bg-violet-500/[0.06] px-3 py-2 text-xs text-violet-300">{notice}</p>
          )}
          {error && (
            <p className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">{error}</p>
          )}

          {loading ? (
            <SkeletonGrid />
          ) : tab === 'explore' ? (
            <>
              {/* Filter pills with counts (design ref) */}
              <div className="mb-4 flex flex-wrap items-center gap-1.5">
                {filterPills.map((pill) => (
                  <button
                    key={pill.id}
                    onClick={() => setOriginFilter(pill.id)}
                    className={`rounded-full px-3.5 py-1.5 text-[12.5px] transition-colors ${
                      originFilter === pill.id
                        ? 'bg-foreground font-extrabold text-background'
                        : 'border border-border/70 font-semibold text-muted-foreground hover:border-border hover:text-foreground'
                    }`}
                  >
                    {pill.label} {pill.count}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {exploreSkills.map((skill) => (
                  <SkillCard
                    key={skill.slug}
                    slug={skill.slug}
                    name={skill.name}
                    summary={skill.summary}
                    originLabel={builtinSlugs.has(skill.slug) ? 'Built-in' : 'Curated'}
                    verified={builtinSlugs.has(skill.slug)}
                    affordance={<IncludedPill />}
                  />
                ))}
              </div>
            </>
          ) : mine.length === 0 ? (
            <div className="flex min-h-[380px] flex-col items-center justify-center text-center">
              <Sparkle weight="duotone" className="size-10 text-violet-400/70" />
              <p className="mt-6 text-base font-semibold">No skills yet</p>
              <p className="mx-auto mt-1.5 max-w-sm text-sm text-muted-foreground">
                Write or upload a playbook — how you like decks structured, your
                brand voice, a code-review checklist — and Galexia will use it
                whenever the task matches.
              </p>
              <Button
                size="sm"
                className="mt-6 rounded-full bg-violet-500 px-4 font-semibold text-white hover:bg-violet-400"
                onClick={openCreateDialog}
              >
                <Plus className="mr-1.5 size-4" weight="bold" />
                Create your first skill
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {mine.map((skill) => (
                <SkillCard
                  key={skill.id}
                  slug={skill.slug}
                  name={skill.name}
                  summary={skill.summary}
                  originLabel="Yours"
                  dimmed={!skill.isActive}
                  metaRight={new Date(skill.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  affordance={
                    <Switch
                      checked={skill.isActive}
                      onCheckedChange={(checked) => handleToggle(skill, checked)}
                      aria-label={skill.isActive ? 'Deactivate skill' : 'Activate skill'}
                    />
                  }
                  hoverActions={
                    <>
                      <RoundIconButton label="Edit skill" onClick={() => openEditDialog(skill)}>
                        <PencilSimple className="size-3.5" />
                      </RoundIconButton>
                      <RoundIconButton label="Delete skill" onClick={() => handleDelete(skill)}>
                        <Trash className="size-3.5" />
                      </RoundIconButton>
                    </>
                  }
                />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* New / edit skill dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit skill' : 'New skill'}</DialogTitle>
            <DialogDescription>
              A skill is a reusable playbook. Galexia reads the summary every
              chat and loads the full instructions when the task matches.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">Name</p>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Pitch-deck structure"
                maxLength={120}
              />
            </div>

            <div>
              <p className="mb-1.5 text-xs font-medium text-muted-foreground">Summary (one line — when should Galexia reach for this?)</p>
              <Input
                value={formSummary}
                onChange={(e) => setFormSummary(e.target.value)}
                placeholder="How I structure investor decks: narrative order, slide budget, tone."
                maxLength={500}
              />
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground">Instructions</p>
                <button
                  type="button"
                  onClick={() => uploadInputRef.current?.click()}
                  className="flex items-center gap-1 rounded-full border border-border/80 px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  <UploadSimple className="size-3.5" />
                  Upload .md
                </button>
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept=".md,.txt"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUploadFile(file);
                    e.target.value = '';
                  }}
                />
              </div>
              <textarea
                value={formContent}
                onChange={(e) => setFormContent(e.target.value)}
                placeholder={'# Pitch-deck playbook\n\nWhen building a deck for me:\n1. Open with the problem, one sentence.\n2. ...'}
                className="min-h-[240px] w-full rounded-lg border bg-background px-3 py-2 font-mono text-xs leading-relaxed outline-none transition-colors focus:border-violet-500/50"
              />
              <p className="mt-1 text-[11px] text-muted-foreground/70">
                Markdown works. {formContent.length.toLocaleString()} / 48,000 characters.
              </p>
            </div>

            {dialogError && (
              <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">{dialogError}</p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                size="sm"
                disabled={saving || !formName.trim() || !formSummary.trim() || !formContent.trim()}
                onClick={handleSave}
                className="rounded-full bg-violet-500 px-4 font-semibold text-white hover:bg-violet-400"
              >
                {saving ? 'Saving…' : 'Save skill'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
